import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import EspeciesForm from "../../components/modals/EspeciesForm";
import { toast } from "react-toastify";
import { Plus, Search, Pencil } from "lucide-react";

const ITEMS_PER_PAGE = 8;

const EspeciesPage = () => {
  const [especies, setEspecies] = useState([]);
  const [filteredEspecies, setFilteredEspecies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedEspecie, setSelectedEspecie] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/util";

  const fetchEspecies = async () => {
    try {
      const res = await axios.get(`${API_URL}/especies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEspecies(res.data);
      setFilteredEspecies(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchEspecies(); }, []); // eslint-disable-line

  useEffect(() => {
    const filtered = especies.filter((a) =>
      a.detalle.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEspecies(filtered);
    setCurrentPage(1);
  }, [searchTerm, especies]);

  const handleSave = async (formData) => {
    try {
      if (selectedEspecie) {
        await axios.put(`${API_URL}/update_especie/${selectedEspecie.codigo}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Especie actualizada correctamente");
      } else {
        await axios.post(`${API_URL}/new_especie`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Especie creada correctamente");
      }
      setShowModal(false);
      fetchEspecies();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || "Error inesperado";
      toast.error(msg);
    }
  };

  const totalPages = Math.ceil(filteredEspecies.length / ITEMS_PER_PAGE);
  const currentItems = filteredEspecies.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-400" />
          <input
            type="text"
            placeholder="Buscar especie..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 border border-brand-200 rounded-xl py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <Button onClick={() => { setSelectedEspecie(null); setShowModal(true); }} className="gap-2">
          <Plus className="h-4 w-4" /> Nueva especie
        </Button>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto rounded-xl border border-brand-100">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-brand-gradient text-white">
              {["Código", "Especie", ""].map((h, i) => (
                <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider ${i === 2 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-50 bg-white">
            {currentItems.length > 0 ? currentItems.map((c, i) => (
              <tr key={c._id || c.codigo} className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}>
                <td className="px-4 py-3">
                  <span className="font-mono text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded font-semibold">{c.codigo}</span>
                </td>
                <td className="px-4 py-3 text-gray-800 font-medium">{c.detalle}</td>
                <td className="px-4 py-3 text-right">
                  <Button variant="outline" size="sm" onClick={() => { setSelectedEspecie(c); setShowModal(true); }} className="gap-1">
                    <Pencil className="h-3 w-3" /> Editar
                  </Button>
                </td>
              </tr>
            )) : (
              <tr><td colSpan="3" className="py-8 text-center text-gray-400 text-sm italic">No se encontraron resultados</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>{currentItems.length} de {filteredEspecies.length}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>← Anterior</Button>
            <span className="px-3 py-1.5 bg-brand-50 border border-brand-200 rounded-lg text-brand-700 font-medium">{currentPage}/{totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Siguiente →</Button>
          </div>
        </div>
      )}

      {showModal && (
        <EspeciesForm especie={selectedEspecie} onClose={() => setShowModal(false)} onSave={handleSave} />
      )}
    </div>
  );
};

export default EspeciesPage;
